<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <h2 class="mb-5">Exercice Préparatoire pour l'Expérience 3</h2>

      <p class="mb-n3">
        Le but de ce questionnaire est de vous préparer à effectuer l'expérience. Il faut obtenir
        <b>la note minimale de 80%</b> des points AVANT d'arriver à votre séance de laboratoire afin
        d'être capable de compléter l'expérience, sinon vous ne recevrez pas la permission
        d'effectuer l'expérience.
      </p>

      <v-radio-group v-model="inputs.requirements" class="ml-6 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in optionsRequirements"
          :key="'pt-0-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <p class="mb-n3">
        a) En générale, pour une réaction à l'équilibre, si un des réactifs est entièrement utilisé,
        la réaction changera vers la direction dont _____________________:
      </p>

      <v-radio-group v-model="inputs.inputA" class="ml-6 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in optionsA"
          :key="'pt-1-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <p class="mb-n3">
        b) Le pH du sang d'un patient est
        <number-value :value="pHB" unit="\text{.}" />
        Lequel des traitements suivants suggérez vous?
      </p>

      <v-radio-group v-model="inputs.inputB" class="ml-6 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in optionsB"
          :key="'pt-2-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <p class="mb-n3">
        c) Les reins fonctionnent pour aider à garder le pH du sang constant. Dans un processus, ils
        changent
        <chemical-latex content="CO2" />
        dans le sang à
        <chemical-latex content="HCO3^-\text{.}" />
        Ceci _____________________:
      </p>

      <v-radio-group v-model="inputs.inputC" class="ml-6 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in optionsC"
          :key="'pt-3-' + option.value"
          class="my-1"
          :value="option.value"
        >
          <template #label>
            <stemble-latex :content="option.text" />
          </template>
        </v-radio>
      </v-radio-group>

      <p class="mb-2">
        d) Dans cette expérience, on produira des déchets chimiques aqueux qui devraient être traîté
        comment? Sélectionnez toutes les bonnes réponses.
      </p>

      <p v-for="option in optionsD" :key="'pt-4-' + option.value" class="mb-0 mt-0">
        <v-checkbox v-model="inputs.inputD" :value="option.value" class="pl-8 mb-n4 mt-0">
          <template v-slot:label>
            <stemble-latex :content="option.text" />
          </template>
        </v-checkbox>
      </p>

      <p class="mb-2 mt-6">
        e) La deuxième partie de l'expérience pourrait être plus facile à comprendre si, avant
        l'expérience, je recherche _____________________ (sélectionnez toutes les bonnes réponses):
      </p>

      <p v-for="option in optionsE" :key="'pt-5-' + option.value" class="mb-0 mt-0">
        <v-checkbox v-model="inputs.inputE" :value="option.value" class="pl-8 mb-n4 mt-0">
          <template v-slot:label>
            <stemble-latex :content="option.text" />
          </template>
        </v-checkbox>
      </p>
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import NumberValue from '@/tasks/components/NumberValue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';

export default {
  name: 'UOttawaPrelab3Fr',
  components: {ChemicalLatex, NumberValue, StembleLatex},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        requirements: null,
        inputA: null,
        inputB: null,
        inputC: null,
        inputD: [],
        inputE: [],
      },
      optionsRequirements: [
        {
          text: "Je comprends et j'accepte le besoin de la note minimale pour le questionnaire.",
          value: 'agree',
        },
        {
          text: "Je n'accepte pas le besoin d'obtenir la note minimale pour le questionnaire.",
          value: 'disagree',
        },
      ],
      optionsA: [
        {text: 'plus de réactifs se produisent', value: 'formMoreReacts'},
        {text: 'plus de produits se produisent', value: 'formMoreProducts'},
        {text: 'rien ne change', value: 'doesNothing'},
      ],
      optionsB: [
        {text: 'Encouragez des inspirations profondes', value: 'deepBreathing'},
        {text: "N'encouragez pas des inspirations profondes", value: 'noDeepBreathing'},
      ],
      optionsC: [
        {text: 'ne changera pas le pH du sang', value: 'noChange'},
        {text: 'diminuera le pH du sang', value: 'lowerPH'},
        {text: 'augmentera le pH du sang', value: 'raisePH'},
      ],
      optionsD: [
        {
          text: 'Déposés dans le contenant pour les déchets chimiques organiques',
          value: 'organicWaste',
        },
        {
          text: 'Déposés dans le contenant pour les déchets chimiques aqueux',
          value: 'aqueousWaste',
        },
        {text: "Déposés dans l'évier", value: 'drain'},
        {
          text: "Déposés dans le contenant des déchets chimiques pour l'argent, s'il y a l'ion argent dans le mélange",
          value: 'silverWaste',
        },
        {text: 'Évaporés complètement et essuyés', value: 'evaporatedToDryness'},
      ],
      optionsE: [
        {text: "l'équilibre", value: 'eqm'},
        {text: 'la formation des complexes', value: 'complex'},
        {text: 'la solubilité', value: 'solEqm'},
        {text: 'le principe de le Châtelier', value: 'leChat'},
      ],
    };
  },
  computed: {
    pHB() {
      return this.taskState.getValueBySymbol('pHB').content;
    },
  },
};
</script>
<style scoped></style>
